import React from 'react';
import PropTypes from 'prop-types';

import {Button} from 'views/components';
import {translate} from 'views/module';

import SubscribeUserDialog from '../dialogs/SubscribeUserDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';

const SmsCommunication = (props) => {
  const {clientBackend, relationshipData, smsData, isDisabled} = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [confirmDialogProps, setConfirmDialogProps] = React.useState({});
  const [smsNumber, setSmsNumber] = React.useState(smsData.smsNumber ?? '');

  React.useEffect(() => {
    setSmsNumber(smsData.smsNumber);
  }, [smsData.smsNumber]);

  const createSmsSubscription = async (smsNumber) => {
    setIsLoading(true);
    await clientBackend.communication.createSmsSubscription(
        relationshipData.relationshipRn,
        smsNumber,
        () => setIsLoading(false),
    );
  };

  const handleSubmitSmsNumber = (input) => {
    createSmsSubscription(input);
    setShowDialog(false);
  };

  const updateSmsSubscription = async () => {
    setIsLoading(true);
    await clientBackend.communication.updateSmsSubscription(
        relationshipData.relationshipRn,
        smsNumber,
        !(smsData.communicationStatus === 'ACTIVE'),
        () => setIsLoading(false),
    );
  };

  const handleOpenDialog = () => {
    setSmsNumber(smsNumber.trim());
    setShowDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmDialog(false);
  };

  const getButtonConfig = () => {
    if (smsData.communicationStatus === 'ACTIVE') {
      return {
        text: translate('client.ClientDetail.Relationships.Contacts.States.Active'),
        action: () => {
          setConfirmDialogProps({
            title: translate(
                'client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmDeactivate.Title',
            ),
            message: translate(
                'client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmDeactivate.Message',
            ),
            confirmText: translate('client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmDeactivate.ConfirmButton'),
          });
          setConfirmAction(() => updateSmsSubscription);
          setShowConfirmDialog(true);
        },
      };
    } else if (smsData.communicationStatus === 'INACTIVE') {
      return {
        text: translate('client.ClientDetail.Relationships.Contacts.States.Reactivate'),
        action: () => {
          setConfirmDialogProps({
            title: translate('client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmReactivate.Title'),
            message: translate('client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmReactivate.Message'),
            confirmText: translate('client.ClientDetail.Relationships.Contacts.Dialogs.ConfirmReactivate.ConfirmButton'),
          });
          setConfirmAction(() => updateSmsSubscription);
          setShowConfirmDialog(true);
        },
      };
    } else if (smsData.communicationStatus === 'NOT_SET_UP') {
      return {text: translate('client.ClientDetail.Relationships.Contacts.States.Setup'), action: handleOpenDialog};
    } else {
      return;
    }
  };

  const buttonConfig = getButtonConfig();
  if (!buttonConfig) {
    return null;
  }
  return (
    <>
      <Button
        onClick={buttonConfig.action}
        variant='contained'
        size='small'
        disabled={isLoading || isDisabled}
        sx={{color: 'color.white'}}
      >
        {buttonConfig.text}
      </Button>
      {
        showDialog &&
        <SubscribeUserDialog
          open={showDialog}
          onClose={() => {
            setShowDialog(false);
          }}
          onSubmit={handleSubmitSmsNumber}
          initialChannelValue={smsNumber}
          isLoading={isLoading}
          channelType='Sms'
          channel='Communication'
          errorMessage={translate('client.ClientDetail.Relationships.Contacts.Dialogs.SmsHelperText.FailRegexMessage')}
          regex={/^\+?1?\s*(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/}
        />
      }
      {
        showConfirmDialog &&
        <ConfirmDialog
          open={showConfirmDialog}
          title={confirmDialogProps.title}
          message={confirmDialogProps.message}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirm}
          confirmText={confirmDialogProps.confirmText}
          cancelText={translate('client.ClientDetail.Relationships.Contacts.Dialogs.Cancel')}
          isLoading={isLoading}
        />
      }
    </>
  );
};

SmsCommunication.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  smsData: PropTypes.shape({
    smsNumber: PropTypes.string.isRequired,
    communicationStatus: PropTypes.string,
  }).isRequired,
  relationshipData: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

export default SmsCommunication;
